import { ApiRequestError, executeSdkEnvelopeRequest } from './api-request.util';
import { adminHighlightSourcesApi } from './sdk-client';

export class AdminHighlightSourceError extends ApiRequestError {
  constructor(
    status: number,
    message: string,
    name = 'AdminHighlightSourceError',
    errorCode?: string,
  ) {
    if (errorCode) {
      super(status, message, name, errorCode);
    } else {
      super(status, message, name);
    }
  }
}

export type HighlightCurrentSource = 'ott' | 'youtube';

export type AdminHighlightSourceLeague = {
  id: string;
  name: string;
  currentSource: HighlightCurrentSource;
  youtubeFallbackEnabled: boolean;
  activePlatformName: string | null;
};

export type AdminHighlightSourceSport = {
  id: string;
  name: string;
  leagues: AdminHighlightSourceLeague[];
};

export type AdminHighlightSourceList = {
  sports: AdminHighlightSourceSport[];
};

export type UpdateHighlightSourceInput = {
  youtube_fallback_enabled: boolean;
  preferred_source: HighlightCurrentSource;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asBoolean(value: unknown, fallback = true): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function parseCurrentSource(value: unknown): HighlightCurrentSource {
  return value === 'ott' ? 'ott' : 'youtube';
}

function parseLeague(value: unknown): AdminHighlightSourceLeague | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  const activePlatformName = value['activePlatformName'] ?? value['active_platform_name'];
  return {
    id: value.id,
    name: asString(value['name']),
    currentSource: parseCurrentSource(value['currentSource'] ?? value['current_source']),
    youtubeFallbackEnabled: asBoolean(
      value['youtubeFallbackEnabled'] ?? value['youtube_fallback_enabled'],
      true,
    ),
    activePlatformName: typeof activePlatformName === 'string' ? activePlatformName : null,
  };
}

function parseSport(value: unknown): AdminHighlightSourceSport | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  const leaguesRaw = value['leagues'];
  return {
    id: value.id,
    name: asString(value['name']),
    leagues: Array.isArray(leaguesRaw)
      ? leaguesRaw
          .map(parseLeague)
          .filter((item): item is AdminHighlightSourceLeague => item != null)
      : [],
  };
}

function parseList(json: unknown): AdminHighlightSourceList {
  const envelope = isRecord(json) ? json : {};
  const data = isRecord(envelope['data']) ? envelope['data'] : envelope;
  const sportsRaw = data['sports'];
  return {
    sports: Array.isArray(sportsRaw)
      ? sportsRaw
          .map(parseSport)
          .filter((item): item is AdminHighlightSourceSport => item != null)
      : [],
  };
}

export async function listAdminHighlightSources(): Promise<AdminHighlightSourceList> {
  return executeSdkEnvelopeRequest({
    request: () => adminHighlightSourcesApi.adminHighlightSourceControllerListV1Raw(),
    parse: parseList,
    fallback: 'Failed to load highlight sources',
    ErrorClass: AdminHighlightSourceError,
  });
}

export async function updateAdminHighlightSource(
  leagueId: string,
  input: UpdateHighlightSourceInput,
): Promise<AdminHighlightSourceLeague> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminHighlightSourcesApi.adminHighlightSourceControllerUpdateV1Raw({
        leagueId,
        updateHighlightSourceDto: {
          youtube_fallback_enabled: input.youtube_fallback_enabled,
          preferred_source: input.preferred_source,
        },
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const mapped = parseLeague(envelope['data']);
      if (mapped) return mapped;
      throw new AdminHighlightSourceError(500, 'Failed to update highlight source');
    },
    fallback: 'Failed to update highlight source',
    ErrorClass: AdminHighlightSourceError,
  });
}
